import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Auth0ClientService, AuthService, AuthState } from '@auth0/auth0-angular';
import { IdToken } from '@auth0/auth0-spa-js';
import { TranslocoService } from '@jsverse/transloco';
import { filter, firstValueFrom, take, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UsersClient } from './dataservice.svc';
import { ApplicationInsightsService } from './telemetry.service';
import { UserContextService } from './user-context.service';

@Injectable({ providedIn: 'root' })
export class UserResolverService {
    private readonly destroyRef = inject(DestroyRef);
    private _translate = inject(TranslocoService);
    private authService = inject(AuthService);
    private auth0Client = inject(Auth0ClientService);
    private authState = inject(AuthState);
    private usersClient = inject(UsersClient);
    private telemetry = inject(ApplicationInsightsService);
    private userContextService = inject(UserContextService);

    private readonly namespace: string = 'https://statusnow.com/';
    private readonly storeContextRepairCacheKey = 'stm.store-context-repair';

    private activeLanguage: string | undefined;
    private readonly _userInfo = signal<UserInfo | null>(null);
    readonly userInfo = this._userInfo.asReadonly();
    readonly userInfo$ = toObservable(this.userInfo).pipe(filter((userInfo): userInfo is UserInfo => userInfo !== null));
    private currentClaims?: IdToken;
    private currentUserInfo?: UserInfo;
    private isRepairingStoreLocation = false;

    constructor() {
        this.authService.idTokenClaims$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (claims) => {
                if (claims) {
                    void this.resolveUserInfo(claims);
                }
            },
            error: (error) => {
                this.telemetry.trackException({ exception: error instanceof Error ? error : new Error(this.describeAuthError(error)) }, { source: 'auth0.idTokenClaims$' });
            }
        });
    }

    private async resolveUserInfo(claims: IdToken): Promise<void> {
        this.currentClaims = claims;
        let userInfo = this.toUserInfo(claims);
        const cachedContext = this.getCachedStoreContext(claims);

        this.logStoreContext(
            `Auth0 claims received ${JSON.stringify({
                storeNumber: userInfo.StoreId,
                locID: userInfo.LocID
            })}`
        );

        if (cachedContext && userInfo.StoreId === cachedContext.storeNumber && userInfo.LocID == null) {
            userInfo.LocID = cachedContext.locID;
            this.logStoreContext(`Using cached repaired LocID for ${userInfo.StoreId}`);
            this.publishUserInfo(userInfo);
            return;
        }

        if (cachedContext && !this.matchesStoreContext(claims, cachedContext.storeNumber, cachedContext.locID)) {
            const authoritativeContext = await this.tryResolveAuthoritativeStoreContext(userInfo.Email);
            if (authoritativeContext) {
                userInfo.StoreId = authoritativeContext.storeNumber;
                userInfo.LocID = authoritativeContext.locID;

                if (this.matchesStoreContext(claims, userInfo.StoreId, userInfo.LocID)) {
                    this.clearCachedStoreContext();
                } else {
                    this.cacheStoreContext(claims, userInfo.StoreId, userInfo.LocID);
                }

                this.publishUserInfo(userInfo);
                return;
            }
        } else if (cachedContext) {
            this.clearCachedStoreContext();
        }

        if (userInfo.StoreId && userInfo.LocID == null) {
            if (this.isRepairingStoreLocation) {
                return;
            }

            this.isRepairingStoreLocation = true;
            try {
                this.logStoreContext(`LocID missing; calling users_EnsureStoreLocation for ${userInfo.StoreId}`);
                const repairedContext = await firstValueFrom(this.usersClient.users_EnsureStoreLocation());
                userInfo.StoreId = repairedContext.storeNumber ?? userInfo.StoreId;
                userInfo.LocID = this.toLocID(repairedContext.locID);
                this.cacheStoreContext(claims, userInfo.StoreId, userInfo.LocID);
                this.logStoreContext(
                    `Ensure completed ${JSON.stringify({
                        storeNumber: userInfo.StoreId,
                        locID: userInfo.LocID
                    })}`
                );

                const refreshedClaims = await this.tryRefreshClaims();
                if (refreshedClaims) {
                    userInfo = this.toUserInfo(refreshedClaims);
                }
            } catch (error) {
                console.error('Failed to repair Auth0 store location metadata', error);
            } finally {
                this.isRepairingStoreLocation = false;
            }
        }

        this.publishUserInfo(userInfo);
    }

    private async tryResolveAuthoritativeStoreContext(email: string): Promise<{ storeNumber: string; locID: number } | undefined> {
        if (!email) {
            return undefined;
        }

        try {
            this.logStoreContext('Cached selection differs from Auth0 claims; checking current user metadata');
            const user = await firstValueFrom(this.usersClient.users_GetAuth0UserByEmail(email));
            const metadata = user.userMetadata as Record<string, unknown> | null | undefined;
            const storeNumber = typeof metadata?.['storeNumber'] === 'string' ? metadata['storeNumber'].trim() : '';
            const locID = this.toLocID(metadata?.['locID']);

            if (!storeNumber || locID === undefined) {
                this.warnStoreContext('Current Auth0 user metadata does not contain a complete store context');
                return undefined;
            }

            this.logStoreContext(`Using current Auth0 user metadata ${JSON.stringify({ storeNumber, locID })}`);
            return { storeNumber, locID };
        } catch (error) {
            this.warnStoreContext(`Failed to check current Auth0 user metadata (${this.describeAuthError(error)})`);
            return undefined;
        }
    }

    public async refreshStoreContext(storeNumber?: string, locID?: number): Promise<void> {
        this.logStoreContext(`Store selection saved ${JSON.stringify({ storeNumber, locID })}`);
        this.clearCachedStoreContext();
        const refreshedClaims = await this.tryRefreshClaims(storeNumber, this.toLocID(locID));
        if (refreshedClaims) {
            this.publishUserInfo(this.toUserInfo(refreshedClaims));
            return;
        }

        const userInfo = Object.assign(new UserInfo(), this.currentUserInfo);
        userInfo.StoreId = storeNumber ?? userInfo.StoreId;
        userInfo.LocID = this.toLocID(locID);
        if (this.currentClaims) {
            this.cacheStoreContext(this.currentClaims, userInfo.StoreId, userInfo.LocID);
        }
        this.publishUserInfo(userInfo);
    }

    private async tryRefreshClaims(expectedStoreNumber?: string, expectedLocID?: number): Promise<IdToken | undefined> {
        try {
            this.logStoreContext('Attempting silent Auth0 claim refresh');
            // Use the underlying client so a missing refresh token does not reach AuthService.error$
            // and trigger AppComponent's interactive login recovery.
            const accessToken = await this.auth0Client.getTokenSilently({ cacheMode: 'off' });
            if (!accessToken) {
                throw new Error('Silent Auth0 refresh returned no access token');
            }

            const refreshedClaimsPromise = firstValueFrom(
                this.authService.idTokenClaims$.pipe(
                    filter((value): value is IdToken => this.matchesStoreContext(value, expectedStoreNumber, expectedLocID)),
                    take(1),
                    timeout(environment.httpRequestTimeoutMs)
                )
            );

            this.authState.setAccessToken(accessToken);
            this.authState.refresh();
            const refreshedClaims = await refreshedClaimsPromise;
            this.logStoreContext('Silent Auth0 claim refresh succeeded');
            return refreshedClaims;
        } catch (error) {
            this.warnStoreContext(`Silent Auth0 claim refresh failed; using the endpoint response locally (${this.describeAuthError(error)})`);
            return undefined;
        }
    }

    private matchesStoreContext(value: IdToken | null | undefined, expectedStoreNumber?: string, expectedLocID?: number): value is IdToken {
        if (!value) {
            return false;
        }

        const storeNumber = this.getStringClaim(value as Record<string, unknown>, 'storeNumber');
        const locID = this.getLocID(value);
        return locID != null && (!expectedStoreNumber || storeNumber === expectedStoreNumber) && (expectedLocID === undefined || locID === expectedLocID);
    }

    private toUserInfo(token: IdToken): UserInfo {
        const claims = token as Record<string, unknown>;
        const userInfo = new UserInfo();
        userInfo.StoreId = this.getStringClaim(claims, 'storeNumber');
        userInfo.LocID = this.getLocID(token);
        userInfo.CustomerId = this.getStringClaim(claims, 'customerID');
        userInfo.UserId = token.sub ?? '';
        userInfo.Email = this.getStringClaim(claims, 'email') ?? token.email ?? '';
        userInfo.profileImage = token.picture ?? '';
        userInfo.fullname = this.getStringClaim(claims, 'name') ?? '';
        const isASM = claims[this.namespace + 'isASM'];
        userInfo.IsASM = isASM === true || isASM === 'true';
        userInfo.FirstName = token.given_name ?? '';
        userInfo.LastName = token.family_name ?? '';
        userInfo.DefaultLanguage = this.getStringClaim(claims, 'defaultLanguage') ?? '';
        userInfo.MobileNumber = this.getStringClaim(claims, 'mobileNumber') ?? '';
        return userInfo;
    }

    private getStringClaim(claims: Record<string, unknown>, claimName: string): string | undefined {
        const customValue = claims[this.namespace + claimName];
        if (typeof customValue === 'string' && customValue) {
            return customValue;
        }

        const standardValue = claims[claimName];
        return typeof standardValue === 'string' && standardValue ? standardValue : undefined;
    }

    private getLocID(token: IdToken | null | undefined): number | undefined {
        return this.toLocID(token?.[this.namespace + 'locID']);
    }

    private toLocID(value: unknown): number | undefined {
        const locID = typeof value === 'number' ? value : Number(value);
        return Number.isInteger(locID) && locID > 0 ? locID : undefined;
    }

    private getCachedStoreContext(claims: IdToken): StoreContextRepairCache | undefined {
        const tokenExpiresAt = this.getTokenExpiresAt(claims);
        if (!claims.sub || tokenExpiresAt === undefined) {
            return undefined;
        }

        try {
            const cachedValue = localStorage.getItem(this.storeContextRepairCacheKey);
            if (!cachedValue) {
                return undefined;
            }

            const cachedContext = JSON.parse(cachedValue) as Partial<StoreContextRepairCache>;
            const userId = typeof cachedContext.userId === 'string' ? cachedContext.userId : undefined;
            const storeNumber = typeof cachedContext.storeNumber === 'string' ? cachedContext.storeNumber.trim() : '';
            const locID = this.toLocID(cachedContext.locID);
            if (userId === undefined || userId !== claims.sub || !storeNumber || cachedContext.expiresAt !== tokenExpiresAt || tokenExpiresAt <= Date.now() || locID === undefined) {
                this.clearCachedStoreContext();
                return undefined;
            }

            return {
                userId,
                storeNumber,
                locID,
                expiresAt: tokenExpiresAt
            };
        } catch {
            this.clearCachedStoreContext();
            return undefined;
        }
    }

    private cacheStoreContext(claims: IdToken, storeNumber?: string, locID?: number): void {
        const tokenExpiresAt = this.getTokenExpiresAt(claims);
        if (!claims.sub || !storeNumber || locID === undefined || tokenExpiresAt === undefined || tokenExpiresAt <= Date.now()) {
            return;
        }

        const cachedContext: StoreContextRepairCache = {
            userId: claims.sub,
            storeNumber,
            locID,
            expiresAt: tokenExpiresAt
        };

        try {
            localStorage.setItem(this.storeContextRepairCacheKey, JSON.stringify(cachedContext));
        } catch {
            // Storage is an optimization; the endpoint remains the fallback when it is unavailable.
        }
    }

    private getTokenExpiresAt(claims: IdToken): number | undefined {
        return typeof claims.exp === 'number' && Number.isFinite(claims.exp) ? claims.exp * 1000 : undefined;
    }

    public clearCachedStoreContext(): void {
        try {
            localStorage.removeItem(this.storeContextRepairCacheKey);
        } catch {
            // Nothing to clear when browser storage is unavailable.
        }
    }

    private publishUserInfo(userInfo: UserInfo): void {
        this.logStoreContext(
            `Publishing userInfo ${JSON.stringify({
                storeNumber: userInfo.StoreId,
                locID: userInfo.LocID
            })}`
        );
        this.currentUserInfo = userInfo;
        this._userInfo.set(userInfo);
        this.userContextService.publish(userInfo);
        this.applyLanguage(userInfo);
    }

    private logStoreContext(message: string): void {
        if (!environment.production) {
            console.log(`[StoreContext] ${message}`);
        }
    }

    private warnStoreContext(message: string): void {
        if (!environment.production) {
            console.warn(`[StoreContext] ${message}`);
        }
    }

    private describeAuthError(error: unknown): string {
        if (error && typeof error === 'object') {
            const authError = error as { error?: unknown; message?: unknown };
            if (typeof authError.error === 'string') {
                return authError.error;
            }
            if (typeof authError.message === 'string') {
                return authError.message;
            }
        }

        return 'unknown error';
    }

    private applyLanguage(userInfo: UserInfo): void {
        if (userInfo.DefaultLanguage) {
            this.activeLanguage = userInfo.DefaultLanguage;
        } else {
            const browserLanguage = navigator.language.split('-')[0];
            const supportedLanguages = this._translate.getAvailableLangs() as string[];
            this.activeLanguage = supportedLanguages.includes(browserLanguage) ? browserLanguage : 'en';
        }

        if (this._translate.getActiveLang() !== this.activeLanguage) {
            this._translate.setActiveLang(this.activeLanguage ?? 'en');
        }
    }
}

export class UserInfo {
    StoreId: string | undefined;
    LocID: number | undefined;
    CustomerId: string | undefined;
    UserId: string | undefined;
    Email = '';
    profileImage = '';
    fullname = '';
    IsASM = false;
    FirstName = '';
    LastName = '';
    MobileNumber = '';
    DefaultLanguage = '';
}

type StoreContextRepairCache = {
    userId: string;
    storeNumber: string;
    locID: number;
    expiresAt: number;
};
